import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { RefreshCw, CheckCircle2, XCircle, MinusCircle, Activity, AlertTriangle, ChevronDown, ArrowDownLeft, ArrowUpRight, KeyRound, Search } from 'lucide-react';

const STATUS_STYLES = {
    success: { cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
    failed: { cls: 'bg-rose-100 text-rose-700', Icon: XCircle },
    skipped: { cls: 'bg-slate-100 text-slate-500', Icon: MinusCircle },
    pending: { cls: 'bg-amber-100 text-amber-700', Icon: Activity },
};

const CREDIT_FILTERS = ['all', 'success', 'failed', 'skipped'];

const Code = ({ value }) => (
    <pre className="text-xs bg-slate-900 text-slate-200 rounded-lg p-3 overflow-x-auto max-h-64">
        {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
);

export default function AdminLoyaltyCredits() {
    const [tab, setTab] = useState('outbound');
    const [data, setData] = useState(null);
    const [inbound, setInbound] = useState(null);
    const [tokenLogs, setTokenLogs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [phoneInput, setPhoneInput] = useState('');
    const [phone, setPhone] = useState('');
    const [open, setOpen] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (filter !== 'all') params.status = filter;
            if (phone) params.phone = phone;
            const [c, i, t] = await Promise.all([
                api.get('/admin/loyalty/point-credits', { params }),
                api.get('/admin/loyalty/inbound-logs', { params: { limit: 200 } }),
                api.get('/admin/loyalty/token-logs', { params: { limit: 200 } }),
            ]);
            setData(c.data);
            setInbound(i.data);
            setTokenLogs(t.data);
        } catch {
            toast.error('Failed to load API monitor');
        } finally {
            setLoading(false);
        }
    }, [filter, phone]);

    useEffect(() => { load(); }, [load]);

    const summary = data?.summary || {};
    const credits = data?.items || [];
    const inboundItems = inbound?.items || [];
    const tokenItems = tokenLogs?.items || [];
    const tenant = data?.tenant_token;

    return (
        <div className="space-y-6" data-testid="admin-loyalty-credits">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-display font-bold text-slate-900 flex items-center gap-2">
                        <Activity className="w-6 h-6 text-amber-500" /> Loyalty API Monitor
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Full request &amp; response for both inbound (Loyalty→LMS) and outbound (LMS→Loyalty) API calls.
                    </p>
                </div>
                <button onClick={load} data-testid="loyalty-credits-refresh"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </button>
            </div>

            {/* Tenant token status */}
            {tenant && (
                <div className={`rounded-xl border p-4 flex items-start gap-3 ${tenant.has_token ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="tenant-token-status">
                    <KeyRound className={`w-5 h-5 shrink-0 mt-0.5 ${tenant.has_token ? 'text-emerald-600' : 'text-amber-600'}`} />
                    <div className="text-sm">
                        <b>Tenant Loyalty token:</b> {tenant.has_token ? 'active' : 'NOT available'}
                        {tenant.source_key_masked && <> · from key <code className="bg-white/60 px-1 rounded">{tenant.source_key_masked}</code></>}
                        {tenant.expires_at && <> · expires {new Date(tenant.expires_at).toLocaleString()}</>}
                        {!tenant.has_token && tenant.gen_response && (
                            <div className="text-amber-800 mt-1">Last generation error: <code>{JSON.stringify(tenant.gen_response)}</code></div>
                        )}
                        <div className="text-xs text-slate-500 mt-1">Generated when the Loyalty app calls <code>/partner/token</code> with a <code>zeapl_key</code>.</div>
                    </div>
                </div>
            )}
            {data && !data.configured && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="loyalty-not-configured">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800"><b>Point Credit endpoint not configured</b> (`LOYALTY_POINT_CREDIT_URL` empty). All credits are skipped.</div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-2 border-b border-slate-200">
                <button onClick={() => setTab('inbound')} data-testid="tab-inbound"
                    className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px inline-flex items-center gap-2 ${tab === 'inbound' ? 'border-amber-500 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
                    <ArrowDownLeft className="w-4 h-4" /> Inbound (Partner API) {inbound ? `· ${inboundItems.length}` : ''}
                </button>
                <button onClick={() => setTab('outbound')} data-testid="tab-outbound"
                    className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px inline-flex items-center gap-2 ${tab === 'outbound' ? 'border-amber-500 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
                    <ArrowUpRight className="w-4 h-4" /> Outbound (Point Credits) {data ? `· ${credits.length}` : ''}
                </button>
                <button onClick={() => setTab('tokens')} data-testid="tab-tokens"
                    className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px inline-flex items-center gap-2 ${tab === 'tokens' ? 'border-amber-500 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
                    <KeyRound className="w-4 h-4" /> Token Generation {tokenLogs ? `· ${tokenItems.length}` : ''}
                </button>
            </div>

            {/* INBOUND */}
            {tab === 'inbound' && (
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                    {loading && <div className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</div>}
                    {!loading && inboundItems.length === 0 && (
                        <div className="px-4 py-10 text-center text-slate-400 text-sm" data-testid="inbound-empty">No inbound partner calls recorded yet.</div>
                    )}
                    {!loading && inboundItems.map((it) => {
                        const isOpen = open === it.id;
                        const okCls = it.status_code < 400 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700';
                        return (
                            <div key={it.id} className="border-b border-slate-50 last:border-0" data-testid={`inbound-row-${it.id}`}>
                                <button onClick={() => setOpen(isOpen ? null : it.id)}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-slate-50 text-left">
                                    <span className="text-slate-400 text-xs w-40 shrink-0">{new Date(it.created_at).toLocaleString()}</span>
                                    <span className="font-mono text-xs font-bold text-slate-700 w-14 shrink-0">{it.method}</span>
                                    <span className="font-mono text-xs text-slate-600 flex-1 truncate">{it.path}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${okCls}`}>{it.status_code}</span>
                                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {isOpen && (
                                    <div className="px-4 pb-4 grid md:grid-cols-2 gap-3 bg-slate-50/60">
                                        <div>
                                            <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Request</div>
                                            <Code value={{ query: it.query, headers: it.request_headers, body: it.request_body }} />
                                        </div>
                                        <div>
                                            <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Response ({it.status_code})</div>
                                            <Code value={it.location ? { redirect_to: it.location, body: it.response_body } : it.response_body} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* OUTBOUND */}
            {tab === 'outbound' && (
                <>
                    <div className="flex items-center gap-2 flex-wrap">
                        {CREDIT_FILTERS.map((f) => (
                            <button key={f} onClick={() => setFilter(f)} data-testid={`loyalty-filter-${f}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize ${filter === f ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                                {f}{f !== 'all' && summary[f] != null ? ` · ${summary[f]}` : ''}
                            </button>
                        ))}
                        <div className="relative ml-auto">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                value={phoneInput}
                                onChange={(e) => setPhoneInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') setPhone(phoneInput.trim()); }}
                                placeholder="Search mobile number…"
                                data-testid="loyalty-phone-search"
                                className="pl-9 pr-20 py-1.5 rounded-full text-sm ring-1 ring-slate-200 outline-none focus:ring-amber-400 w-60" />
                            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                {phone && (
                                    <button onClick={() => { setPhone(''); setPhoneInput(''); }} data-testid="loyalty-phone-clear"
                                        className="text-xs text-slate-400 hover:text-rose-500 font-bold px-1">Clear</button>
                                )}
                                <button onClick={() => setPhone(phoneInput.trim())} data-testid="loyalty-phone-search-btn"
                                    className="text-xs font-bold text-amber-600 hover:text-amber-700 px-1">Search</button>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                        {loading && <div className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</div>}
                        {!loading && credits.length === 0 && (
                            <div className="px-4 py-10 text-center text-slate-400 text-sm" data-testid="loyalty-credits-empty">No credit attempts recorded yet.</div>
                        )}
                        {!loading && credits.map((it) => {
                            const s = STATUS_STYLES[it.status] || STATUS_STYLES.pending;
                            const isOpen = open === it.id;
                            return (
                                <div key={it.id} className="border-b border-slate-50 last:border-0" data-testid={`loyalty-credit-row-${it.id}`}>
                                    <button onClick={() => setOpen(isOpen ? null : it.id)}
                                        className="w-full grid grid-cols-12 gap-2 px-4 py-3 text-sm hover:bg-slate-50 text-left items-center">
                                        <div className="col-span-3 text-slate-500 text-xs">{new Date(it.created_at).toLocaleString()}</div>
                                        <div className="col-span-2 font-mono text-slate-800">{it.phone || '—'}</div>
                                        <div className="col-span-1 font-bold text-slate-900">{it.points}</div>
                                        <div className="col-span-2 font-mono text-xs text-slate-500 truncate">{it.reference || '—'}</div>
                                        <div className="col-span-2">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${s.cls}`}>
                                                <s.Icon className="w-3.5 h-3.5" />{it.status}
                                            </span>
                                        </div>
                                        <div className="col-span-2 flex items-center justify-between">
                                            <span className="text-slate-500 text-xs">{it.http_status ?? '—'}</span>
                                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                        </div>
                                    </button>
                                    {isOpen && (
                                        <div className="px-4 pb-4 space-y-3 bg-slate-50/60">
                                            <div className="text-xs text-slate-500">{it.reason}</div>
                                            <div className="grid md:grid-cols-2 gap-3">
                                                <div>
                                                    <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Token generation</div>
                                                    <Code value={it.token_generation} />
                                                </div>
                                                <div>
                                                    <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Credit request</div>
                                                    {it.endpoint && <div className="text-xs text-slate-600 break-all mb-1"><b>POST</b> {it.endpoint}</div>}
                                                    <Code value={{ headers: it.request_headers, body: it.request_body }} />
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Credit response</div>
                                                <Code value={it.response} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* TOKEN GENERATION */}
            {tab === 'tokens' && (
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                    {loading && <div className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</div>}
                    {!loading && tokenItems.length === 0 && (
                        <div className="px-4 py-10 text-center text-slate-400 text-sm" data-testid="token-logs-empty">
                            No generate-token calls recorded yet. (Triggered when the Loyalty app calls <code>/partner/token</code> with a <code>zeapl_key</code>.)
                        </div>
                    )}
                    {!loading && tokenItems.map((it) => {
                        const isOpen = open === it.id;
                        const okCls = it.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700';
                        const Icon = it.status === 'success' ? CheckCircle2 : XCircle;
                        return (
                            <div key={it.id} className="border-b border-slate-50 last:border-0" data-testid={`token-log-row-${it.id}`}>
                                <button onClick={() => setOpen(isOpen ? null : it.id)}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-slate-50 text-left">
                                    <span className="text-slate-400 text-xs w-40 shrink-0">{new Date(it.created_at).toLocaleString()}</span>
                                    <span className="font-mono text-xs font-bold text-slate-700 w-20 shrink-0 capitalize">{it.scope}</span>
                                    <span className="font-mono text-xs text-slate-600 flex-1 truncate">key {it.key_masked} · {it.source}</span>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${okCls}`}>
                                        <Icon className="w-3.5 h-3.5" />{it.http_status ?? it.status}
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {isOpen && (
                                    <div className="px-4 pb-4 grid md:grid-cols-2 gap-3 bg-slate-50/60">
                                        <div>
                                            <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Request</div>
                                            {it.endpoint && <div className="text-xs text-slate-600 break-all mb-1"><b>POST</b> {it.endpoint}</div>}
                                            <Code value={{ headers: it.request_headers, body: it.request_body }} />
                                        </div>
                                        <div>
                                            <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Response ({it.http_status ?? '—'})</div>
                                            <Code value={it.token_masked ? { token: it.token_masked, body: it.response } : it.response} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
